import React, { useState, useEffect } from "react";
import axios from "axios";
import VideoPlaylist from "./VideoPlaylist";
import AudioPlayer from "./AudioPlayer";
import { Link, useNavigate } from "react-router-dom";
import useAuthCheck from "./useAuthCheck";


const MembersDashboard = ({ user }) => {

    useAuthCheck();

    const navigate = useNavigate();

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
    };

    const [events, setEvents] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [resources, setResources] = useState([]);
    const [blogs, setBlogs] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [images, setImages] = useState([]);
    const [audio, setAudio] = useState([]);
    const [videos, setVideos] = useState([]);
    const [loadingSections, setLoadingSections] = useState({
        events: true,
        announcements: true,
        resources: true,
        blogs: true,
        documents: true,
        images: true,
        audio: true,
        videos: true,
    });

    const fetchData = async (url, setData, section) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setData(response.data);
        } catch (error) {
            console.error(`Error loading ${section}:`, error);
        } finally {
            setLoadingSections((prev) => ({ ...prev, [section]: false }));
        }
    };

    useEffect(() => {
        fetchData("/api/calendar/formatted-events", setEvents, "events");
        fetchData("/api/announcements?limit=3", setAnnouncements, "announcements");
        // fetchData("/api/members/resources", setResources, "resources");
        fetchData("/api/blog/public", setBlogs, "blogs");
        // fetchData("/api/members/documents", setDocuments, "documents");
        // fetchData("/api/members/images", setImages, "images");
        // fetchData("/api/members/audio", setAudio, "audio");
        // fetchData("/api/members/videos", setVideos, "videos");
    }, []);



    return (
        <div className="members-dashboard container mx-auto p-6">
            {/* User Greeting */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">
                    Welcome, {user?.name || "Member"}!
                </h1>
                {user?.roles.includes("admin") && (
                    <Link
                        to="/admin"
                        className="bg-red-500 text-white px-4 py-2 rounded-lg"
                    >
                        Admin Tools
                    </Link>

                )}

            </div>

            {/* Dashboard Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Announcements */}
                <div className="card border p-4 rounded-lg bg-white shadow">
                    <h2 className="text-xl font-semibold mb-4">Announcements</h2>
                    {loadingSections.announcements ? (
                        <p>Loading announcements...</p>
                    ) : announcements.length > 0 ? (
                        <ul className="space-y-4">
                            {announcements.map((announcement) => {
                                const startDate = announcement.scheduleDate
                                    ? new Date(announcement.scheduleDate).toLocaleString()
                                    : "Not scheduled";
                                const expireDate = announcement.expireDate
                                    ? new Date(announcement.expireDate).toLocaleString()
                                    : "No expiration";

                                return (
                                    <li key={announcement._id} className="p-4 border rounded-lg bg-gray-100">
                                        <h3 className="font-bold">{announcement.title}</h3>
                                        <p>{announcement.description}</p>
                                        {announcement.imageUrl && (
                                            <img
                                                src={announcement.imageUrl}
                                                alt={announcement.title}
                                                className="mt-2"
                                            />
                                        )}
                                        {announcement.linkUrl && (
                                            <a
                                                href={announcement.linkUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-500 hover:underline mt-2 inline-block"
                                            >
                                                {announcement.linkText}
                                            </a>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p>No announcements available.</p>
                    )}
                </div>

                {/* Events */}
                <div className="card border p-4 rounded-lg bg-white shadow">
                    <h2 className="text-xl font-semibold mb-4">Upcoming Events</h2>
                    {loadingSections.events ? (
                        <p>Loading events...</p>
                    ) : events.length > 0 ? (
                        <ul className="space-y-4">
                            {events.map((event) => (
                                <li key={event.id} className="p-2 bg-gray-100 rounded">
                                    <h3 className="font-bold">{event.title}</h3>
                                    <p>{event.date}</p>
                                    <p>{event.description}</p>
                                    <p>{event.location}</p>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p>No upcoming events.</p>
                    )}
                </div>

                {/* Blogs */}
                <div className="card border p-4 rounded-lg bg-white shadow">
                    <h2 className="text-xl font-semibold mb-4">Recent Blog Posts</h2>
                    {loadingSections.blogs ? (
                        <p>Loading blogs...</p>
                    ) : blogs.length > 0 ? (
                        <ul className="space-y-4">
                            {blogs.map((blog) => {
                                const date = new Date(blog.createdAt);
                                const formattedDate = new Intl.DateTimeFormat("en-US", {
                                    dateStyle: "medium",
                                    timeStyle: "short",
                                }).format(date);
                                return (
                                    <li key={blog.id} className="p-2 bg-gray-100 rounded">
                                        <h3 className="font-bold">{blog.title}</h3>
                                        <p>{blog.author}</p>
                                        <p>{formattedDate}</p>
                                        <a
                                            href={`/blog/${blog.slug}`}
                                            className="text-blue-500 hover:underline"
                                        >
                                            Read More
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p>No blog posts available.</p>
                    )}


                    {/* Documents */}
                    {documents.length > 0 && (
                        <div className="card border p-4 rounded-lg bg-white shadow">
                            <h2 className="text-xl font-semibold mb-4">Documents</h2>
                            <ul className="space-y-4">
                                {documents.map((doc) => (
                                    <li key={doc.id} className="p-4 border rounded-lg bg-gray-100">
                                        <h3 className="font-bold">{doc.name}</h3>
                                        <a
                                            href={doc.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-500 hover:underline"
                                        >
                                            Download Document
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Images */}
                    {images.length > 0 && (
                        <div className="card border p-4 rounded-lg bg-white shadow">
                            <h2 className="text-xl font-semibold mb-4">Images</h2>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {images.map((image) => (
                                    <div key={image.id} className="p-2 border rounded-lg bg-gray-100">
                                        <img src={image.url} alt={image.name} className="rounded-lg"/>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Audio */}
                    {audio.length > 0 && (
                        <div className="card border p-4 rounded-lg bg-white shadow">
                            <h2 className="text-xl font-semibold mb-4">Audio</h2>
                            <AudioPlayer playlist={audio}/>
                        </div>
                    )}

                    {/* Videos */}
                    {videos.length > 0 && (
                        <div className="card border p-4 rounded-lg bg-white shadow">
                            <h2 className="text-xl font-semibold mb-4">Videos</h2>
                            <VideoPlaylist videos={videos}/>
                        </div>
                    )}

                </div>
            </div>
            <div className="p-4 text-center">
            <button
                onClick={handleLogout}
                className="bg-red-500 text-white px-4 py-2 rounded-lg"
            >
                Logout
            </button>
            </div>
        </div>
    );
};

export default MembersDashboard;
